import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { modulesAPI } from '../services/api';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import MessageDisplay from '../components/MessageDisplay';
import useAsyncOperation from '../hooks/useAsyncOperation';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import StaffPageShell from '../components/StaffPageShell';

const ModuleManagementPage = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const { loading, error, success, execute, setError, setSuccess } = useAsyncOperation();
  const [editingModule, setEditingModule] = useState(null);
  const [formData, setFormData] = useState({
    moduleName: '',
    tentativeDuration: '',
    numberOfTestPapers: 0,
    numberOfMockInterviews: 0,
    numberOfLabs: 0,
    numberOfAssignments: 0,
    numberOfMinorProjects: 0,
    numberOfMajorProjects: 0,
    isActive: true
  });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await execute(async () => await modulesAPI.getAll());
      if (response?.data !== undefined) {
        setModules(normalizeApiListResponse(response.data));
      } else {
        setModules([]);
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
      setModules([]);
    }
  };

  const handleCreate = () => {
    setEditingModule(null);
    setFormData({
      moduleName: '',
      tentativeDuration: '',
      numberOfTestPapers: 0,
      numberOfMockInterviews: 0,
      numberOfLabs: 0,
      numberOfAssignments: 0,
      numberOfMinorProjects: 0,
      numberOfMajorProjects: 0,
      isActive: true
    });
    setFieldErrors({});
    setShowModal(true);
  };

  const handleEdit = (module) => {
    setEditingModule(module);
    setFormData({
      moduleName: module.moduleName || '',
      tentativeDuration: module.tentativeDuration?.toString() || '',
      numberOfTestPapers: module.numberOfTestPapers || 0,
      numberOfMockInterviews: module.numberOfMockInterviews || 0,
      numberOfLabs: module.numberOfLabs || 0,
      numberOfAssignments: module.numberOfAssignments || 0,
      numberOfMinorProjects: module.numberOfMinorProjects || 0,
      numberOfMajorProjects: module.numberOfMajorProjects || 0,
      isActive: module.isActive !== undefined ? module.isActive : true
    });
    setFieldErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.moduleName || !formData.moduleName.trim()) {
      setError('Module name is required');
      setFieldErrors({ moduleName: 'Module name is required' });
      return;
    }
    
    // Prepare submit data
    const submitData = {
      moduleName: formData.moduleName.trim(),
      numberOfTestPapers: parseInt(formData.numberOfTestPapers) || 0,
      numberOfMockInterviews: parseInt(formData.numberOfMockInterviews) || 0,
      numberOfLabs: parseInt(formData.numberOfLabs) || 0,
      numberOfAssignments: parseInt(formData.numberOfAssignments) || 0,
      numberOfMinorProjects: parseInt(formData.numberOfMinorProjects) || 0,
      numberOfMajorProjects: parseInt(formData.numberOfMajorProjects) || 0,
      isActive: formData.isActive !== undefined ? formData.isActive : true
    };
    
    // Add optional fields only if they have values
    if (formData.tentativeDuration && formData.tentativeDuration.trim()) {
      const duration = parseInt(formData.tentativeDuration);
      if (!isNaN(duration) && duration > 0) {
        submitData.tentativeDuration = duration;
      }
    }
    
    console.log('📝 Submitting module data:', submitData);
    
    // Clear previous field errors
    setFieldErrors({});
    
    try {
      const response = await execute(async () => {
        if (editingModule) {
          return await modulesAPI.update(editingModule.id, submitData);
        } else {
          return await modulesAPI.create(submitData);
        }
      }, { 
        successMessage: `Module ${editingModule ? 'updated' : 'created'} successfully!`,
        throwOnError: true
      });
      
      if (response) {
        setShowModal(false);
        setEditingModule(null);
        setFieldErrors({});
        fetchModules();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      // Handle validation errors
      if (err?.response?.status === 400 && err?.response?.data?.error?.details) {
        const validationErrors = {};
        err.response.data.error.details.forEach((detail) => {
          validationErrors[detail.field] = detail.message;
        });
        setFieldErrors(validationErrors);
        
        // Also set a general error message
        const errorMessages = err.response.data.error.details
          .map(d => `${d.field}: ${d.message}`)
          .join('\n');
        setError(`Validation failed:\n${errorMessages}`);
      } else {
        // Handle other errors
        const errorMessage = err?.response?.data?.error?.message || 
                            err?.response?.data?.message || 
                            err?.message || 
                            'An error occurred while saving the module';
        setError(errorMessage);
      }
    }
  };

  const handleDelete = async (module) => {
    if (!window.confirm(`Are you sure you want to delete module "${module.moduleName}"?`)) {
      return;
    }
    
    const response = await execute(async () => await modulesAPI.delete(module.id), {
      successMessage: 'Module deleted successfully!'
    });
    
    if (response) {
      fetchModules();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  return (
    <StaffPageShell title="Modules" wide>
        <div className="flex justify-end items-center mb-6">
          <button
            onClick={handleCreate}
            className="px-6 py-2 rounded-lg font-medium theme-button-primary flex items-center gap-2"
          >
            <span>+</span> Create Module
          </button>
        </div>

        <MessageDisplay error={error} success={success} />

        {loading && <LoadingSpinner />}

        <div className="theme-card rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.isArray(modules) && modules.map((module) => (
              <div
                key={module.id}
                className="p-4 border rounded-lg theme-border hover:shadow-lg transition-shadow"
                style={{ backgroundColor: 'var(--color-surface)' }}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold theme-text">{module.moduleName}</h3>
                    {module.tentativeDuration && (
                      <p className="text-sm theme-text-secondary">Duration: {module.tentativeDuration} days</p>
                    )}
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      module.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {module.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="space-y-1 text-sm theme-text-secondary mb-3">
                  <p>Test Papers: {module.numberOfTestPapers || 0}</p>
                  <p>Mock Interviews: {module.numberOfMockInterviews || 0}</p>
                  <p>Labs: {module.numberOfLabs || 0}</p>
                  <p>Assignments: {module.numberOfAssignments || 0}</p>
                  <p>Minor Projects: {module.numberOfMinorProjects || 0}</p>
                  <p>Major Projects: {module.numberOfMajorProjects || 0}</p>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(module)}
                    className="flex-1 px-3 py-1.5 text-sm rounded theme-button-secondary"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(module)}
                    className="flex-1 px-3 py-1.5 text-sm rounded bg-red-500 text-white hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {modules.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="theme-text-secondary">No modules found. Create one to get started.</p>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingModule(null);
            setFieldErrors({});
            setError('');
          }}
          title={editingModule ? 'Edit Module' : 'Create Module'}
          size="large"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Module Name *
              </label>
              <input
                type="text"
                required
                value={formData.moduleName}
                onChange={(e) => {
                  setFormData({ ...formData, moduleName: e.target.value });
                  if (fieldErrors.moduleName) {
                    setFieldErrors({ ...fieldErrors, moduleName: '' });
                  }
                }}
                className={`w-full px-3 py-2 rounded-lg theme-input ${
                  fieldErrors.moduleName ? 'border-2 border-red-500' : ''
                }`}
                placeholder="e.g., Digital Design Fundamentals"
              />
              {fieldErrors.moduleName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.moduleName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Tentative Duration (Days)
              </label>
              <input
                type="number"
                value={formData.tentativeDuration}
                onChange={(e) => {
                  setFormData({ ...formData, tentativeDuration: e.target.value });
                  if (fieldErrors.tentativeDuration) {
                    setFieldErrors({ ...fieldErrors, tentativeDuration: '' });
                  }
                }}
                className={`w-full px-3 py-2 rounded-lg theme-input ${
                  fieldErrors.tentativeDuration ? 'border-2 border-red-500' : ''
                }`}
                placeholder="e.g., 30"
                min="1"
              />
              {fieldErrors.tentativeDuration && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.tentativeDuration}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Test Papers
                </label>
                <input
                  type="number"
                  value={formData.numberOfTestPapers}
                  onChange={(e) => setFormData({ ...formData, numberOfTestPapers: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg theme-input"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Mock Interviews
                </label>
                <input
                  type="number"
                  value={formData.numberOfMockInterviews}
                  onChange={(e) => setFormData({ ...formData, numberOfMockInterviews: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg theme-input"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Labs
                </label>
                <input
                  type="number"
                  value={formData.numberOfLabs}
                  onChange={(e) => setFormData({ ...formData, numberOfLabs: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg theme-input"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Assignments
                </label>
                <input
                  type="number"
                  value={formData.numberOfAssignments}
                  onChange={(e) => setFormData({ ...formData, numberOfAssignments: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg theme-input"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Minor Projects
                </label>
                <input
                  type="number"
                  value={formData.numberOfMinorProjects}
                  onChange={(e) => setFormData({ ...formData, numberOfMinorProjects: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg theme-input"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Major Projects
                </label>
                <input
                  type="number"
                  value={formData.numberOfMajorProjects}
                  onChange={(e) => setFormData({ ...formData, numberOfMajorProjects: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg theme-input"
                  min="0"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm theme-text">
                Active
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : editingModule ? 'Update Module' : 'Create Module'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingModule(null);
                  setFieldErrors({});
                }}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
    </StaffPageShell>
  );
};

export default ModuleManagementPage;
